import { useCallback, useMemo } from 'react';
import { Table } from '@carboledger/ui';
import { buildTableLabels } from '@/config/tableLabels';
import {
  useTableState,
  useTranslations,
  useGenuinelyEmpty,
  UnitTypeContext,
  useUnitTypeHeader,
} from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import {
  useGetIncomingDeclarationsQuery,
  useGetDeclarationFilterOptionsQuery,
} from '../api/declarationsApi';
import { buildDeclarationsColumns, buildFilters } from '../constants/declarationsColumns';
import { buildUnitHeaderOptions } from '../constants/declarationUnits';
import { isConsumedDeclaration } from '../constants/declarations.constants';

const getDeclarationRowId = (row) => row.id;

/**
 * Props:
 *   extraFilters             filters merged on top of the panel's, e.g. the inventory drill-down's
 *   certificateType   the module's `CertificateType`, which narrows the list
 *   emptyState        <Table>'s emptyState — the caller owns its copy and CTA
 *   noResults         <Table>'s noResults
 *   onView            (row) => void — makes the id cell a link; omit for plain text
 *   onOpenDiscrepancy (row, unitType) => void — the row's discrepancy modal
   onViewFile        (row) => void — opens the row’s declaration file
 *   rowActions        <Table>'s rowActions
 *   rowMenuActions    <Table>'s rowMenuActions
 *   bulkActions       <Table>'s bulkActions — selectable follows from it
 *   tableRef          forwarded to <Table>, for clearSelection
 */
export const DeclarationsTable = ({
  extraFilters,
  certificateType,
  emptyState,
  noResults,
  onView,
  onOpenDiscrepancy,
  onViewFile,
  onEmptyChange,
  rowActions,
  rowMenuActions,
  bulkActions,
  tableRef,
}) => {
  const t = useTranslations();
  const table = useTableState();

  const queryArgs = useMemo(
    () => ({ certificateType, ...table.queryParams, extraFilters }),
    [certificateType, table.queryParams, extraFilters],
  );
  const { data, isFetching, isError } = useGetIncomingDeclarationsQuery(queryArgs);
  const rows = data?.rows ?? EMPTY_LIST;
  const total = data?.totalCount ?? 0;

  useGenuinelyEmpty({ table, isFetching, isError, total }, onEmptyChange);

  const { unitHeaderSelect, contextValue, selections } = useUnitTypeHeader(
    rows,
    buildUnitHeaderOptions,
    t,
  );

  // The modal needs the unit the row is currently showing, which only the table knows.
  const handleOpenDiscrepancy = useCallback(
    (row) => onOpenDiscrepancy?.(row, selections.get(row.id) ?? row.unitType),
    [onOpenDiscrepancy, selections],
  );

  const columns = useMemo(
    () =>
      buildDeclarationsColumns(t, {
        onOpenDiscrepancy: handleOpenDiscrepancy,
        onView,
        onViewFile,
        unitHeaderSelect,
      }),
    [t, handleOpenDiscrepancy, onView, onViewFile, unitHeaderSelect],
  );

  const filterOptionsArgs = useMemo(
    () => ({ certificateType, extraFilters }),
    [certificateType, extraFilters],
  );
  const { data: filterOptions } = useGetDeclarationFilterOptionsQuery(filterOptionsArgs);

  const filterDefinitions = useMemo(
    () =>
      buildFilters(t, {
        materialOptions: filterOptions?.materialOptions ?? EMPTY_LIST,
        supplierOptions: filterOptions?.supplierOptions ?? EMPTY_LIST,
      }),
    [t, filterOptions],
  );
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const handleFilterChange = useCallback(
    (values) => {
      table.setFilters(values);
      table.setPage(1);
    },
    [table],
  );

  return (
    <UnitTypeContext.Provider value={contextValue}>
      <Table
        ref={tableRef}
        columns={columns}
        data={rows}
        total={total}
        loading={isFetching}
        getRowId={getDeclarationRowId}
        searchable
        selectable={Boolean(bulkActions)}
        isRowDisabled={isConsumedDeclaration}
        bulkActions={bulkActions}
        onSearchChange={handleSearchChange}
        filters={filterDefinitions}
        onFilterChange={handleFilterChange}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        rowActions={rowActions}
        rowMenuActions={rowMenuActions}
        actionsHeader={t('FIELD_ACTION')}
        defaultPageSize={25}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />
    </UnitTypeContext.Provider>
  );
};
